(function () {
  'use strict';

  const number = (value) => {
    if ((typeof value !== 'string' && typeof value !== 'number') || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const reviewCount = (value) => {
    const count = number(value);
    return Number.isSafeInteger(count) && count >= 0 ? count : null;
  };

  function shopifyRating(metafield) {
    if (!metafield || (metafield.type && metafield.type !== 'rating')) return null;
    let data = metafield.value;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (_error) { return null; }
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const value = number(data.value);
    const max = number(data.scale_max);
    const min = data.scale_min == null ? 0 : number(data.scale_min);
    if (value == null || max == null || min == null || max <= 0 || min < 0 || min >= max || value <= 0 || value < min || value > max) return null;
    return { value, max };
  }

  function getRating(product) {
    const count = reviewCount(product?.graphql?.rccRatingCount?.value);
    // An explicit zero in Shopify must not resurrect a stale imported rating.
    if (count === 0) return null;
    const source = shopifyRating(product?.graphql?.rccRating);
    if (source) return { ...source, count };

    const imported = product?.rating;
    if (!imported || typeof imported !== 'object') return null;
    const value = number(imported.averageRating);
    const importedCount = reviewCount(imported.ratingCount);
    if (value == null || value <= 0 || value > 5 || importedCount === 0) return null;
    return { value, max: 5, count: importedCount };
  }

  function formatter(locale, maximumFractionDigits) {
    try { return new Intl.NumberFormat(locale || 'de-DE', { maximumFractionDigits }); }
    catch (_error) { return new Intl.NumberFormat('de-DE', { maximumFractionDigits }); }
  }

  function accessibleLabel(config, rating) {
    const format = formatter(config.locale, 2);
    const countFormat = formatter(config.locale, 0);
    const values = {
      rating: format.format(rating.value),
      max: format.format(rating.max),
      count: rating.count == null ? '' : countFormat.format(rating.count),
    };
    values.rating_value = values.rating;
    values.rating_max = values.max;
    values.rating_count = values.count;
    let template = config.ratingLabel || '{rating} von {max} Sternen';
    const hasCount = /\{\{?\s*(?:rating_count|count)\s*\}\}?/.test(template);
    if (hasCount && rating.count == null) template = '{rating} von {max} Sternen';
    const label = String(template).replace(/\{\{?\s*(rating_value|rating_max|rating_count|rating|max|count)\s*\}\}?/g, (_match, key) => values[key]);
    return rating.count != null && !hasCount
      ? `${label}, ${values.count} ${rating.count === 1 ? 'Bewertung' : 'Bewertungen'}` : label;
  }

  class RccProductRating extends HTMLElement {
    static get graphqlRequirements() {
      return {
        graphql: [
          'rccRating: metafield(namespace: "reviews", key: "rating") { value type }',
          'rccRatingCount: metafield(namespace: "reviews", key: "rating_count") { value type }',
        ],
      };
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    connectedCallback() {
      this.render();
    }

    render() {
      const config = window.MundizioCards || {};
      const rating = config.showRating === false ? null : getRating(this._product);
      this.replaceChildren();
      this.hidden = !rating;
      if (!rating) {
        this.removeAttribute('role');
        this.removeAttribute('aria-label');
        return;
      }

      this.setAttribute('role', 'img');
      this.setAttribute('aria-label', accessibleLabel(config, rating));

      const stars = document.createElement('span');
      stars.className = 'rating inline-block align-middle';
      stars.setAttribute('aria-hidden', 'true');
      const fill = document.createElement('span');
      fill.className = 'rating__stars';
      // Match snippets/rating.liquid's star rounding and existing theme styles.
      const whole = Math.floor(rating.value);
      const decimal = Number((rating.value - whole).toFixed(10));
      const rounded = Math.min(rating.max, whole + (decimal >= 0.3 && decimal <= 0.7 ? 0.5 : decimal > 0.7 ? 1 : 0));
      fill.setAttribute('style', `--rating: ${rounded}; --rating-max: ${rating.max};`);
      fill.textContent = '★★★★★';
      stars.append(fill);
      this.append(stars);

      if (rating.count != null) {
        const count = document.createElement('span');
        count.className = 'rating__count inline-block align-middle text-sm';
        count.setAttribute('aria-hidden', 'true');
        count.textContent = `(${formatter(config.locale, 0).format(rating.count)})`;
        this.append(count);
      }
    }
  }

  if (!customElements.get('rcc-product-rating')) {
    customElements.define('rcc-product-rating', RccProductRating);
  }
})();
